import { MutableRefObject, useContext, useEffect, useRef } from 'react';
import { ScenarioFormContext } from './contexts';
import { AreaConfigResponse } from './types';
import { isNewConfigSaved, SelectedConfigIds } from './utils';

export const useTypeCurveScenarioForm = () => {
  const context = useContext(ScenarioFormContext);
  if (!context?.form) {
    throw new Error(
      'useTypeCurveScenarioForm must be used within a ScenarioFormProvider'
    );
  }
  return context;
};

/** Extra work a section does when the selected area config changes. */
interface ConfigSwitchHandlers {
  /** Runs when the new, unsaved config was saved and got this id. */
  onSave?: (configId: string) => void;
  /** Runs when the user picked another config. */
  onSwitch?: () => void;
}

/**
 * Follows the selected area config for a section. A change is a save when the
 * new, unsaved config gets its saved id, and a switch otherwise. The returned
 * ref holds the id of the config whose data the section already has: the saved
 * id after a save, so the section does not load that data again, and null
 * after a switch, so it loads the new config's data.
 * @param {AreaConfigResponse | null} selectedAreaConfig - The selected config
 * @param {ConfigSwitchHandlers} handlers - Extra work for a save or a switch
 * @return {MutableRefObject<string | null>} The id of the loaded config
 */
export const useConfigSwitch = (
  selectedAreaConfig: AreaConfigResponse | null,
  handlers: ConfigSwitchHandlers = {}
): MutableRefObject<string | null> => {
  const initializedForConfigRef = useRef<string | null>(null);
  const prevConfigRef = useRef<SelectedConfigIds>({
    id: null,
    elementId: null,
  });
  // The latest handlers, so the effect below runs only when the config changes.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    const previous = prevConfigRef.current;
    const current = {
      id: selectedAreaConfig?.id ?? null,
      elementId: selectedAreaConfig?.elementId ?? null,
    };
    prevConfigRef.current = current;
    if (current.id === previous.id) return;

    if (current.id && isNewConfigSaved(previous, current)) {
      initializedForConfigRef.current = current.id;
      handlersRef.current.onSave?.(current.id);
      return;
    }

    initializedForConfigRef.current = null;
    handlersRef.current.onSwitch?.();
  }, [selectedAreaConfig?.id, selectedAreaConfig?.elementId]);

  return initializedForConfigRef;
};
